import {
  Alert,
  AlertTitle,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { createOtherIncomes } from 'lib/api/conciliations';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { formatMoney, showAlert } from 'lib/functions/utils';
import React from 'react';
import { useDispatch } from 'react-redux';

type PayIn = {
  amount: number;
  amountConciliated: number;
  bankAccountDestinationId: number;
  bankAccountDestinationName: string;
  bankAccountOriginId: number;
  bankAccountOriginName: string;
  businessDestinationIdentifier: string;
  businessDestinationName: string;
  businessOriginIdentifier: string;
  businessOriginName: string;
  createdAt: string;
  id: number;
  status: string;
  statusTranslated: string;
};

interface OtherIncomesDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  setBigOpen: (open: boolean) => void;
  payIn: PayIn;
  setUp: () => void;
}

const OtherIncomesDialog = (props: OtherIncomesDialogProps) => {
  const { payIn, setOpen, setBigOpen, open, setUp } = props;
  const [loading, setLoading] = React.useState(false);

  const dispatch = useDispatch();

  const submitOtherIncomes = async () => {
    setLoading(true);
    try {
      await createOtherIncomes(dispatch, {
        payInId: payIn.id,
      });
      void setUp();
      setBigOpen(false);
      setOpen(false);
      showAlert(dispatch, 'success', 'Otros ingresos generados correctamente');
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al generar otros ingresos');
    }
    setLoading(false);
  };
  const otherIncomesAmount = payIn.amount - payIn.amountConciliated;
  const otherIncomesAmountFormatted = formatMoney(otherIncomesAmount);

  return (
    <Dialog maxWidth="sm" fullWidth open={open}>
      <DialogTitle>Generar otros ingresos</DialogTitle>

      {loading ? (
        <Box
          sx={{
            minHeight: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CenteredCircularProgress type="dialog" />
        </Box>
      ) : (
        <DialogContent sx={{ p: 3 }}>
          <Alert
            severity="info"
            sx={{
              mb: 2,
              bgcolor: '#f0f7ff',
              border: '1px solid #b3d9ff',
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: '#1976d2',
              },
            }}
          >
            <AlertTitle sx={{ color: '#1976d2', fontWeight: 600 }}>
              Información importante
            </AlertTitle>
            <Typography variant="body2" sx={{ color: '#495057', mt: 1 }}>
              Se generará una cuenta por cobrar a nombre de{' '}
              <strong>{payIn.businessOriginName}</strong> |{' '}
              <strong>{payIn.businessOriginIdentifier}</strong> por un total de{' '}
              <strong>{otherIncomesAmountFormatted}</strong>.
            </Typography>
          </Alert>

          <Alert
            severity="success"
            sx={{
              bgcolor: '#f0f9f0',
              border: '1px solid #a5d6a7',
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: '#4caf50',
              },
            }}
          >
            <Typography variant="body2" sx={{ color: '#2e7d32' }}>
              La cuenta por cobrar se pagará automáticamente. El pago recibido
              quedará completado.
            </Typography>
          </Alert>
        </DialogContent>
      )}

      <DialogActions>
        <CustomButton
          onClick={() => setOpen(false)}
          color="secondary"
          disabled={loading}
        >
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={() => submitOtherIncomes()}
          color="primary"
          disabled={loading}
        >
          Generar otros ingresos
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default OtherIncomesDialog;
